/* How a step's payloads read: the JSON tree, and the formatted views of a model call's
   messages, a tool's arguments and result, a turn's and a run's input. The step panel
   (`StepPanel`) draws them; moved out of `TraceDetail` with it. */
import React, { useState } from "react";
import { ChevronDown, Wrench, Zap } from "lucide-react";

import { fmtCost, fmtMs, fmtTokens } from "@/shared/lib/runs";
import { chipCls, EmptyNote, ErrorNote, FieldRow, TruncText } from "./atoms";
import { kindView } from "./views";

import type {
  AgentInputPayload, AssistantPayload, ContentBlock, LlmInputPayload, Span, SpanIo, ToolDef, ToolInputPayload, ToolOutputPayload,
  ToolResultBlock as ToolResultBlockData, ToolUseBlock as ToolUseBlockData, TurnInputPayload,
} from "../types";

/* ------------------------------- JSON viewer ------------------------------- */

function JsonKey({ k }: { k?: string }) {
  if (k == null) return null;
  return (
    <span className="text-[var(--text-secondary)]">
      "{k}"<span className="text-[var(--text-tertiary)]">: </span>
    </span>
  );
}

function JsonLeaf({ k, value, full }: { k?: string; value: unknown; full: boolean }) {
  if (value === null || value === undefined) {
    return <div><JsonKey k={k} /><span className="italic text-[var(--text-quaternary)]">null</span></div>;
  }
  if (typeof value === "string") {
    return (
      <div>
        <JsonKey k={k} />
        <span className="text-[var(--warn-text)] whitespace-pre-wrap [overflow-wrap:anywhere]">"{full ? value : <TruncText text={value} limit={200} mono />}"</span>
      </div>
    );
  }
  return <div><JsonKey k={k} /><span className="text-[var(--info-text)]">{String(value)}</span></div>;
}

function JsonNode({ k, value, depth, full }: { k?: string; value: unknown; depth: number; full: boolean }) {
  // the whole record opens deeper: a tool's text sits four levels down
  const [open, setOpen] = useState(depth < (full ? 6 : 3));
  const isObj = value !== null && typeof value === "object";
  if (!isObj) return <JsonLeaf k={k} value={value} full={full} />;

  const isArr = Array.isArray(value);
  const entries = isArr ? value.map((v, i) => [String(i), v]) : Object.entries(value);
  const unit = isArr ? "item" : "key";
  return (
    <div>
      <button onClick={() => setOpen((v) => !v)} className="press inline-flex items-center gap-0.5 text-left text-[var(--text-primary)]">
        <ChevronDown className={`w-2.5 h-2.5 -ml-0.5 text-[var(--text-quaternary)] transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={2} />
        <JsonKey k={k} />
        <span className="text-[var(--text-tertiary)]">{isArr ? "[" : "{"}</span>
        {!open && <span className="text-[var(--text-quaternary)]"> … {entries.length} {unit}{entries.length === 1 ? "" : "s"} {isArr ? "]" : "}"}</span>}
      </button>
      {open && (
        <>
          <div className="pl-4 border-l border-[var(--text-tertiary)] ml-[3px]">
            {entries.map(([ck, cv]) => <JsonNode key={ck} k={ck} value={cv} depth={depth + 1} full={full} />)}
          </div>
          <span className="text-[var(--text-tertiary)] ml-[3px]">{isArr ? "]" : "}"}</span>
        </>
      )}
    </div>
  );
}

/** `full`: every string whole, as the API sent it — the step panel's Raw JSON. */
export function JsonView({ data, full = false }: { data: unknown; full?: boolean }) {
  if (data == null) return <div className="text-[12.5px] italic text-[var(--text-quaternary)] py-1">null</div>;
  return (
    <div className="font-mono text-[12px] leading-[1.7] min-w-0 [overflow-wrap:anywhere]">
      <JsonNode value={data} depth={0} full={full} />
    </div>
  );
}

/* ------------------------------ pretty viewer ------------------------------ */

/* --- chat-ML content blocks — text, tool_use and tool_result render like the
   payload the API actually saw, Langfuse-style --- */

export function ToolUseBlock({ block }: { block: ToolUseBlockData }) {
  return (
    <div className="rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] px-2.5 py-2 mt-1.5">
      <div className="flex items-center gap-1.5 mb-1 min-w-0">
        <Wrench className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
        <span className="font-mono text-[12px] font-medium text-[var(--text-primary)]">{block.name}</span>
        <span className="ml-auto font-mono text-[10px] text-[var(--text-quaternary)] truncate">{block.id}</span>
      </div>
      <TruncText mono text={JSON.stringify(block.input)} limit={180} className="text-[11.5px] leading-[1.6] text-[var(--text-secondary)]" />
    </div>
  );
}

export function ToolResultBlock({ block }: { block: ToolResultBlockData }) {
  return (
    <div className={`rounded-[8px] px-2.5 py-2 mt-1.5 ring-1 ${
      block.is_error ? "bg-[var(--danger-bg)] ring-[var(--danger-ring)]" : "bg-[var(--surface-sunk)] ring-[var(--hairline)]"
    }`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`font-mono text-[10.5px] font-medium ${block.is_error ? "text-[var(--danger)]" : "text-[var(--text-tertiary)]"}`}>
          tool_result{block.is_error ? " · error" : ""}
        </span>
        <span className="ml-auto font-mono text-[10px] text-[var(--text-quaternary)] truncate">{block.tool_use_id}</span>
      </div>
      <TruncText mono text={block.content ?? "—"} limit={200} className={`text-[11.5px] leading-[1.6] ${block.is_error ? "text-[var(--danger)]" : "text-[var(--text-secondary)]"}`} />
    </div>
  );
}

export function ContentBlocks({ content, expanded = false }: { content?: ContentBlock[] | string | null; expanded?: boolean }) {
  if (content == null) return <span className="text-[12.5px] italic text-[var(--text-quaternary)]">empty</span>;
  if (typeof content === "string")
    return <TruncText text={content} limit={expanded ? Infinity : undefined} className="text-[13px] leading-[1.6] text-[var(--text-primary)]" />;
  return (
    <>
      {content.map((b, i) =>
        b.type === "tool_use" ? <ToolUseBlock key={i} block={b} />
        : b.type === "tool_result" ? <ToolResultBlock key={i} block={b} />
        : <TruncText key={i} text={b.text} limit={expanded ? Infinity : undefined} className="block text-[13px] leading-[1.6] text-[var(--text-primary)]" />
      )}
    </>
  );
}

// Message block — role tag over a left-ruled body, like a transcript excerpt.
export function RoleBlock({ role, text, content, expanded = false }: {
  role?: string;
  text?: string | null;
  content?: ContentBlock[];
  expanded?: boolean;
}) {
  return (
    <div className="min-w-0 break-words pl-2.5 border-l-2 border-[var(--hairline-strong)] py-1.5">
      <div className="text-[11.5px] font-medium text-[var(--text-tertiary)] mb-0.5 capitalize">{role}</div>
      {text != null ? (
        <TruncText text={text} className="text-[13px] leading-[1.6] text-[var(--text-primary)]" />
      ) : (
        <ContentBlocks content={content} expanded={expanded} />
      )}
    </div>
  );
}

/* Tool definitions passed with the request — collapsed to a count by default,
   expands to name + description per tool (schemas live in the raw view). */
export function ToolsField({ tools }: { tools?: ToolDef[] }) {
  const [open, setOpen] = useState(false);
  if (!tools?.length) return null;
  return (
    <div className="py-2 border-b hairline">
      <button
        onClick={() => setOpen((v) => !v)}
        className="press w-full flex items-center gap-1.5 text-left"
      >
        <span className="text-[11.5px] text-[var(--text-tertiary)]">Tools</span>
        <span className={chipCls}>{tools.length}</span>
        <ChevronDown className={`w-3 h-3 ml-auto text-[var(--text-quaternary)] transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={2} />
      </button>
      {open && (
        <div className="mt-1.5 rounded-[8px] ring-1 ring-[var(--hairline)] divide-y divide-[var(--hairline)] overflow-hidden">
          {tools.map((t) => (
            <div key={t.name} className="px-2.5 py-1.5 bg-[var(--surface-sunk)]">
              <div className="font-mono text-[11.5px] font-medium text-[var(--text-primary)]">{t.name}</div>
              <div className="text-[11.5px] leading-[1.5] text-[var(--text-tertiary)] mt-[1px]">{t.description}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LlmInputPretty({ data }: { data: LlmInputPayload }) {
  return (
    <div>
      <FieldRow label="Model"><span className={chipCls}>{data.model}</span></FieldRow>
      <FieldRow label="Sampling">
        <span className="font-mono text-[12.5px] text-[var(--text-primary)]">
          temperature {data.temperature ?? "—"} <span className="text-[var(--text-quaternary)]">·</span> max_tokens {data.max_tokens?.toLocaleString() ?? "—"}
        </span>
      </FieldRow>
      <ToolsField tools={data.tools} />
      {data.system && (
        <FieldRow label="System prompt">
          <TruncText text={data.system} limit={320} className="leading-[1.6]" />
        </FieldRow>
      )}
      <div className="py-2">
        <div className="text-[11.5px] text-[var(--text-tertiary)] mb-1.5">
          Messages · {data.messages.length}
        </div>
        <div className="space-y-2">
          {data.messages.map((m, i) => <RoleBlock key={i} role={m.role} content={m.content} />)}
        </div>
      </div>
    </div>
  );
}

function AssistantPretty({ data, span }: { data: AssistantPayload; span?: Span }) {
  return (
    <div>
      <RoleBlock role={data.role || "assistant"} content={data.content} expanded />
      {(data.usage || data.stop_reason) && (
        <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
          {data.usage && (
            <>
              <span className={chipCls}>↑ {fmtTokens(data.usage.input_tokens || 0)} in</span>
              <span className={chipCls}>↓ {fmtTokens(data.usage.output_tokens || 0)} out</span>
              {/* Absent means nothing was read from cache — comparing that
                  against zero is the check, not an oversight. */}
              {(data.usage.cache_read_input_tokens as number) > 0 && (
                <span className={chipCls} title="Input tokens read from prompt cache (billed at 10%)">
                  <Zap className="w-[10px] h-[10px] text-[var(--text-tertiary)]" strokeWidth={1.75} />
                  {fmtTokens(data.usage.cache_read_input_tokens)} cached
                </span>
              )}
              <span className={chipCls}>Σ {fmtTokens(data.usage.total_tokens ?? (data.usage.input_tokens || 0) + (data.usage.output_tokens || 0))}</span>
            </>
          )}
          {span?.ttft != null && <span className={chipCls} title="Time to first token">TTFT {fmtMs(span.ttft)}</span>}
          {span?.cost != null && <span className={chipCls}>{fmtCost(span.cost)}</span>}
          {data.stop_reason && <span className={chipCls}>stop: {data.stop_reason}</span>}
        </div>
      )}
    </div>
  );
}

function ToolInputPretty({ data }: { data: ToolInputPayload }) {
  const args = Object.entries(data.arguments || {});
  return (
    <div>
      <FieldRow label="Tool">
        <span className="inline-flex items-center gap-2 min-w-0">
          <span className={chipCls}>{data.name}</span>
          <span className="font-mono text-[10.5px] text-[var(--text-quaternary)] truncate">{data.tool_use_id}</span>
        </span>
      </FieldRow>
      {args.length === 0 ? (
        <EmptyNote>No arguments.</EmptyNote>
      ) : (
        args.map(([k, v]) => (
          <FieldRow key={k} label={k}>
            <TruncText mono text={typeof v === "string" ? v : JSON.stringify(v)} className="text-[12.5px] leading-[1.6]" />
          </FieldRow>
        ))
      )}
    </div>
  );
}

function ToolOutputPretty({ data }: { data: ToolOutputPayload }) {
  if (data.error) return <ErrorNote text={data.error} />;
  return <TruncText mono text={data.result ?? "—"} className="block text-[12.5px] leading-[1.7] text-[var(--text-primary)] py-1" />;
}

function TurnInputPretty({ data }: { data: TurnInputPayload }) {
  return (
    <div>
      <FieldRow label="Interface"><span className={chipCls}>{data.interface}</span></FieldRow>
      {data.context_tokens != null && (
        <FieldRow label="Context size">
          <span className="font-mono text-[12.5px]">{fmtTokens(data.context_tokens)} tokens</span>
        </FieldRow>
      )}
      {data.text && <RoleBlock role="user" text={data.text} />}
    </div>
  );
}

function AgentInputPretty({ data }: { data: AgentInputPayload }) {
  return (
    <div>
      <FieldRow label="Source"><span className={chipCls}>{data.source}</span></FieldRow>
      <RoleBlock role="user" text={data.prompt} />
    </div>
  );
}

export function PrettyView({ span, io, which }: { span: Span; io: SpanIo; which: "input" | "output" }) {
  const data = io[which];
  if (data == null) return <EmptyNote>Nothing recorded for this step.</EmptyNote>;
  // Kind-specific renderers win; the four core kinds keep their bespoke views;
  // anything else falls through to JSON, which is always correct.
  const View = kindView(span.kind, which);
  if (View) return <View data={data} span={span} />;
  if (which === "input") {
    if (span.kind === "llm") return <LlmInputPretty data={data} />;
    if (span.kind === "tool") return <ToolInputPretty data={data} />;
    if (span.kind === "turn") return <TurnInputPretty data={data} />;
    if (span.kind === "agent") return <AgentInputPretty data={data} />;
  } else {
    if (span.kind === "tool") return <ToolOutputPretty data={data} />;
    if (data.role) return <AssistantPretty data={data} span={span} />;
  }
  return <JsonView data={data} />;
}
